import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Lets a row finish its exit animation before the data change removes it.
 *
 * Answering a check-in is the interaction the whole product is built around, so
 * it should feel like the card departed rather than blinking out of existence.
 * The write is deliberately delayed by one animation, which is also long enough
 * to read as "that registered".
 */
export const LEAVE_MS = 240;

interface Pending {
  timer: ReturnType<typeof setTimeout>;
  commit: () => void;
}

export function useLeaving() {
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(new Set());
  const pending = useRef(new Map<string, Pending>());

  /**
   * On unmount, FLUSH — do not cancel.
   *
   * Cancelling looked tidier and silently dropped the user's answer if they
   * tapped "Threw it" and switched tabs inside 240ms. An animation must never
   * be able to swallow a write; the delay is a presentation detail, and the
   * answer is the whole point.
   */
  useEffect(() => {
    const map = pending.current;
    return () => {
      for (const { timer, commit } of map.values()) {
        clearTimeout(timer);
        commit();
      }
      map.clear();
    };
  }, []);

  const dismiss = useCallback((id: string, commit: () => void) => {
    if (pending.current.has(id)) return; // already on its way out
    setLeaving((prev) => new Set(prev).add(id));

    const timer = setTimeout(() => {
      pending.current.delete(id);
      setLeaving((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      commit();
    }, LEAVE_MS);

    pending.current.set(id, { timer, commit });
  }, []);

  return { leaving, dismiss };
}
